"use client"

import { useEffect, useState } from "react"
import { useSearchParams, useRouter, usePathname } from "next/navigation"
import { CheckCircle2, Info, X } from "lucide-react"
import { revalidateCreditBalance } from "@/hooks/use-billing"
import { track } from "@/lib/analytics/track"
import {
  purchaseContextFromParams,
  trackPurchase,
} from "@/lib/analytics/billing-events"
import {
  isToolCreditTopUpReturn,
  ToolCreditTopUpReturn,
} from "@/components/marketing/tools/tool-credit-top-up-return"

/**
 * Handles the Stripe checkout return on the tool page itself (no off-page
 * billing routes). Reads ?upgrade=success|cancel, shows an inline banner, and
 * — on success — revalidates the credit balance a few times to ride out the
 * (async) webhook that grants the plan credits, then strips the query param.
 */
export function FalToolUpgradeReturn({ slug }: { slug: string }) {
  const params = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [banner, setBanner] = useState<null | "success" | "cancel">(null)

  useEffect(() => {
    // A credit pack checkout returns with `credit_topup` — confirmed and
    // announced by `ToolCreditTopUpReturn`, never as a plan upgrade.
    if (isToolCreditTopUpReturn(params)) return
    const u = params.get("upgrade")
    if (u !== "success" && u !== "cancel") return

    // One-time banner hydration from the URL on mount; the param is stripped
    // immediately below, so this cannot loop. Safe to set state in this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBanner(u)
    if (u === "success") {
      // Read plan context off the URL BEFORE the query is stripped below, then
      // fire a plan-aware standard Purchase (Meta/GA); fall back to the
      // plan-less funnel event for older links.
      const source = `fal_tool_${slug.replace(/-/g, "_")}`
      const ctx = purchaseContextFromParams(params)
      if (ctx) {
        trackPurchase(ctx.plan, { transactionId: ctx.transactionId, source })
      } else {
        track("upgrade_succeeded", { source })
      }
      // The credit grant lands via the checkout.session.completed webhook, which
      // is async — refresh a few times so the new balance appears without a reload.
      const timers = [0, 1500, 4000, 8000].map((ms) =>
        setTimeout(() => void revalidateCreditBalance(), ms),
      )
      router.replace(pathname, { scroll: false })
      return () => timers.forEach(clearTimeout)
    }
    router.replace(pathname, { scroll: false })
  }, [params, router, pathname, slug])

  if (!banner) return <ToolCreditTopUpReturn />

  const isSuccess = banner === "success"
  return (
    <div
      className={`flex items-center justify-center gap-2 px-4 py-3 text-sm ${
        isSuccess ? "bg-emerald-50 text-emerald-800" : "bg-muted text-muted-foreground"
      }`}
    >
      {isSuccess ? (
        <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
      ) : (
        <Info className="size-4 shrink-0" />
      )}
      <span>
        {isSuccess
          ? "Upgrade complete — your credits are being added. You can generate now."
          : "Checkout cancelled — you were not charged."}
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        className="ml-2 opacity-60 hover:opacity-100"
        onClick={() => setBanner(null)}
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
